import React, { useEffect, useState } from 'react';
import { bookmarksApi } from '../api/client';
import type { Bookmark } from '../api/client';
import './Bookmarks.css';

const Bookmarks: React.FC = () => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'category' | 'created' | 'visited'>('created');
  const [newBookmark, setNewBookmark] = useState({
    title: '',
    url: '',
    description: '',
    category: 'personal' as 'work' | 'personal' | 'learning' | 'entertainment' | 'reference' | 'shopping' | 'social' | 'other',
    tags: '',
    isPrivate: false,
    isFavorite: false
  });

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    try {
      setLoading(true);
      const response = await bookmarksApi.getBookmarks();
      setBookmarks(response.data);
    } catch (err) {
      setError('Failed to load bookmarks');
      console.error('Bookmarks error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBookmark = async () => {
    if (!newBookmark.title.trim() || !newBookmark.url.trim()) return;

    try {
      const bookmarkData = {
        title: newBookmark.title,
        url: newBookmark.url,
        description: newBookmark.description || undefined,
        category: newBookmark.category,
        tags: newBookmark.tags ? newBookmark.tags.split(',').map(t => t.trim()).filter(t => t) : undefined,
        isPrivate: newBookmark.isPrivate,
        isFavorite: newBookmark.isFavorite
      };

      await bookmarksApi.createBookmark(bookmarkData);
      await fetchBookmarks();
      setShowAddModal(false);
      resetNewBookmark();
    } catch (err) {
      console.error('Error adding bookmark:', err);
      setError('Failed to add bookmark');
    }
  };

  const handleDeleteBookmark = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this bookmark?')) return;

    try {
      await bookmarksApi.deleteBookmark(id);
      await fetchBookmarks();
    } catch (err) {
      console.error('Error deleting bookmark:', err);
      setError('Failed to delete bookmark');
    }
  };

  const handleVisitBookmark = async (bookmark: Bookmark) => {
    try {
      await bookmarksApi.visitBookmark(bookmark.id);
      window.open(bookmark.url, '_blank', 'noopener,noreferrer');
      await fetchBookmarks(); // Refresh to update visit count
    } catch (err) {
      console.error('Error tracking visit:', err);
      // Still open the URL even if tracking fails
      window.open(bookmark.url, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleFavorite = async (bookmark: Bookmark) => {
    try {
      await bookmarksApi.updateBookmark(bookmark.id, { isFavorite: !bookmark.isFavorite });
      await fetchBookmarks();
    } catch (err) {
      console.error('Error updating favorite:', err);
      setError('Failed to update favorite');
    }
  };

  const resetNewBookmark = () => {
    setNewBookmark({
      title: '',
      url: '',
      description: '',
      category: 'personal',
      tags: '',
      isPrivate: false,
      isFavorite: false
    });
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'work': return '💼';
      case 'personal': return '👤';
      case 'learning': return '📚';
      case 'entertainment': return '🎬';
      case 'reference': return '📖';
      case 'shopping': return '🛒';
      case 'social': return '👥';
      default: return '🔖';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'work': return '#3498db';
      case 'personal': return '#2ecc71';
      case 'learning': return '#9b59b6';
      case 'entertainment': return '#e74c3c';
      case 'reference': return '#f39c12';
      case 'shopping': return '#e67e22';
      case 'social': return '#1abc9c';
      default: return '#95a5a6';
    }
  };

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  const filteredAndSortedBookmarks = bookmarks
    .filter(bookmark => {
      const matchesCategory = filterCategory === 'all' || bookmark.category === filterCategory;
      const matchesSearch = !searchTerm || 
        bookmark.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookmark.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookmark.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bookmark.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'visited':
          return (b.lastVisited || '').localeCompare(a.lastVisited || '');
        case 'created':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

  if (loading) {
    return <div className="bookmarks-page"><div className="loading">Loading bookmarks...</div></div>;
  }

  if (error) {
    return <div className="bookmarks-page"><div className="error">Error: {error}</div></div>;
  }

  const categories = ['all', 'work', 'personal', 'learning', 'entertainment', 'reference', 'shopping', 'social', 'other'];
  const favoriteBookmarks = bookmarks.filter(b => b.isFavorite);
  const privateBookmarks = bookmarks.filter(b => b.isPrivate);

  return (
    <div className="bookmarks-page">
      <header className="page-header">
        <h1>🔖 Bookmarks</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Bookmark
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="bookmarks-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search bookmarks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <div className="category-filters">
            {categories.map(category => (
              <button
                key={category}
                className={`category-filter ${filterCategory === category ? 'active' : ''}`}
                onClick={() => setFilterCategory(category)}
              >
                {category === 'all' ? 'All' : category.charAt(0).toUpperCase() + category.slice(1)}
                {category !== 'all' && (
                  <span className="category-count">
                    {bookmarks.filter(b => b.category === category).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="sort-control">
            <label htmlFor="sort-select">Sort by:</label>
            <select 
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="sort-select"
            >
              <option value="created">Date Created</option>
              <option value="title">Title</option>
              <option value="category">Category</option>
              <option value="visited">Last Visited</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bookmarks-stats">
        <div className="stat-card">
          <span className="stat-number">{filteredAndSortedBookmarks.length}</span>
          <span className="stat-label">
            {filterCategory === 'all' ? 'Total Bookmarks' : `${filterCategory} Bookmarks`}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{favoriteBookmarks.length}</span>
          <span className="stat-label">Favorites</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{privateBookmarks.length}</span>
          <span className="stat-label">Private</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{categories.slice(1).length}</span>
          <span className="stat-label">Categories</span>
        </div>
      </div>

      <div className="bookmarks-grid">
        {filteredAndSortedBookmarks.map((bookmark) => (
          <div key={bookmark.id} className="bookmark-card">
            <div className="bookmark-header">
              <div className="bookmark-title">
                <span className="bookmark-icon">{getCategoryIcon(bookmark.category)}</span>
                <div className="bookmark-info">
                  <h3 className="bookmark-name">{bookmark.title}</h3>
                  <span className="bookmark-domain">{getDomainFromUrl(bookmark.url)}</span>
                </div>
              </div>
              <div className="bookmark-actions">
                <button
                  className={`favorite-btn ${bookmark.isFavorite ? 'active' : ''}`}
                  onClick={() => toggleFavorite(bookmark)}
                  title={bookmark.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  aria-label={bookmark.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {bookmark.isFavorite ? '⭐' : '☆'}
                </button>
                <button 
                  className="delete-btn"
                  onClick={() => handleDeleteBookmark(bookmark.id)}
                  title="Delete bookmark"
                  aria-label="Delete bookmark"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="bookmark-content" onClick={() => handleVisitBookmark(bookmark)}>
              {bookmark.description && (
                <p className="bookmark-description">{bookmark.description}</p>
              )}

              <div className="bookmark-details">
                <div className="detail-row">
                  <span className="detail-label">Category:</span>
                  <span 
                    className="category-badge"
                    style={{ backgroundColor: getCategoryColor(bookmark.category) }}
                  >
                    {bookmark.category}
                  </span>
                </div>

                {bookmark.visitCount && bookmark.visitCount > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Visits:</span>
                    <span className="detail-value">{bookmark.visitCount}</span>
                  </div>
                )}

                {bookmark.lastVisited && (
                  <div className="detail-row">
                    <span className="detail-label">Last Visited:</span>
                    <span className="detail-value">
                      {new Date(bookmark.lastVisited).toLocaleDateString()}
                    </span>
                  </div>
                )}

                <div className="detail-row">
                  <span className="detail-label">Added:</span>
                  <span className="detail-value">
                    {new Date(bookmark.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {bookmark.tags && bookmark.tags.length > 0 && (
                <div className="bookmark-tags">
                  {bookmark.tags.map((tag, index) => (
                    <span key={index} className="bookmark-tag">{tag}</span>
                  ))}
                </div>
              )}

              <div className="bookmark-badges">
                {bookmark.isPrivate && <span className="private-badge">🔒 Private</span>}
                {bookmark.isFavorite && <span className="favorite-badge">⭐ Favorite</span>}
              </div>
            </div>
          </div>
        ))}

        {filteredAndSortedBookmarks.length === 0 && (
          <div className="no-bookmarks">
            <span className="no-bookmarks-icon">🔖</span>
            <p>{searchTerm || filterCategory !== 'all' 
              ? 'No bookmarks match your search criteria' 
              : 'No bookmarks added yet. Add your first bookmark to get started!'}</p>
          </div>
        )}
      </div>

      {/* Add Bookmark Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Bookmark</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newBookmark.title}
                  onChange={(e) => setNewBookmark({...newBookmark, title: e.target.value})}
                  className="form-input"
                  placeholder="Enter bookmark title..."
                />
              </div>

              <div className="form-group">
                <label>URL *</label>
                <input
                  type="url"
                  value={newBookmark.url}
                  onChange={(e) => setNewBookmark({...newBookmark, url: e.target.value})}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newBookmark.description}
                  onChange={(e) => setNewBookmark({...newBookmark, description: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Brief description of this bookmark..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select 
                    value={newBookmark.category}
                    onChange={(e) => setNewBookmark({...newBookmark, category: e.target.value as any})}
                    className="form-select"
                  >
                    <option value="personal">Personal</option>
                    <option value="work">Work</option>
                    <option value="learning">Learning</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="reference">Reference</option>
                    <option value="shopping">Shopping</option>
                    <option value="social">Social</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={newBookmark.tags}
                    onChange={(e) => setNewBookmark({...newBookmark, tags: e.target.value})}
                    className="form-input"
                    placeholder="tutorial, react, documentation..."
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newBookmark.isFavorite}
                      onChange={(e) => setNewBookmark({...newBookmark, isFavorite: e.target.checked})}
                      className="form-checkbox"
                    />
                    Add to favorites
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newBookmark.isPrivate}
                      onChange={(e) => setNewBookmark({...newBookmark, isPrivate: e.target.checked})}
                      className="form-checkbox"
                    />
                    Mark as private
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddBookmark}
                disabled={!newBookmark.title.trim() || !newBookmark.url.trim()}
              >
                Add Bookmark
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookmarks;